import React, { PropsWithChildren } from 'react';

import Button from '@/core/components/Button';
import Link from '@/core/components/Link';
import { ToastStatus } from '@/core/components/Toast/Toast';
import { useEnvContext } from '@/core/lib/env/env.context';
import { useNativeContext } from '@/core/lib/native/native.context';
import Api from '@/core/lib/new-architecture/api';
import type UserDomain from '@/core/lib/new-architecture/domain/user.domain';
import Query from '@/core/lib/new-architecture/query';
import getToken from '@/core/lib/new-architecture/query/token';
import Store from '@/core/lib/new-architecture/store';
import Routes from '@/core/lib/router/routes';
import { useShareContext } from '@/core/lib/share/share.context';
import { useToastContext } from '@/core/lib/toast/toast.context';
import { useTranslationContext } from '@/core/lib/translation/translation.context';

export const UserPrivateProfileActionsSection: React.FC<{ user: UserDomain; canShare?: boolean }> = ({ user, canShare }) => {
  const { t } = useTranslationContext(['common']);

  return (
    <section className="flex gap-6 max-md:flex-col">
      <Link href={new Routes.ProfileModifyRoute()} variant="secondary" iconLeft="edit" className="block w-full">
        {t('components.profile.public.edit', { ns: 'common' })}
      </Link>
      {canShare && (
        <UserShareActions variant="secondary" user={user}>
          {t('components.profile.private.share', { ns: 'common' })}
        </UserShareActions>
      )}
    </section>
  );
};

export const UserPublicProfileActionsSection: React.FC<{ user: UserDomain; canShare?: boolean }> = ({ user, canShare }) => {
  const toast = useToastContext();
  const { t } = useTranslationContext(['common']);
  const { requestToken, isLoggedIn } = Store.auth.useRequest();
  const { invalidateContact } = Query.user.useInvalidate();

  const toggleFollowProfile = async () => {
    if (user.isFollowed) {
      await Api.mutation.user.deleteContact(user.uuid, await getToken(requestToken));
      toast?.open(t('components.profile.public.follow-delete', { ns: 'common' }), 2000, ToastStatus.SUCCESS);
    } else {
      await Api.mutation.user.follow(user.uuid, await getToken(requestToken));
      toast?.open(t('components.profile.public.follow-save', { ns: 'common' }), 2000, ToastStatus.SUCCESS);
    }
    await invalidateContact();
  };

  return (
    <section className="flex gap-6 max-md:flex-col">
      {canShare && (
        <UserShareActions variant="primary" user={user}>
          {t('components.profile.public.share', { ns: 'common' })}
        </UserShareActions>
      )}
      {isLoggedIn && (
        <Button variant="secondary" onClick={toggleFollowProfile} iconLeft="bookmark">
          {user.isFollowed ? t('components.profile.public.unfollow', { ns: 'common' }) : t('components.profile.public.follow', { ns: 'common' })}
        </Button>
      )}
    </section>
  );
};

const UserShareActions: React.FC<PropsWithChildren<{ user: UserDomain; variant: 'primary' | 'secondary' }>> = ({ user, children, variant }) => {
  const { BASE_URL } = useEnvContext();
  const { isNative } = useNativeContext();

  const toast = useToastContext();
  const { t } = useTranslationContext(['common']);
  const { canShare, shareUrl } = useShareContext();

  const onShareDonation = async () => {
    if (isNative && (await canShare())) {
      const url = `${BASE_URL}${new Routes.PublicProfileRoute(user.uuid).resolve()}`;
      const params = {
        title: '',
        text: t('components.profile.public.share-link-text', { ns: 'common' }),
        url,
        dialogTitle: t('components.profile.public.share-link', { ns: 'common' }),
      };
      shareUrl(params);
      return;
    }
    await navigator.clipboard.writeText(`${window.location.origin}${new Routes.PublicProfileRoute(user.uuid).resolve()}`);

    toast?.open(t('components.profile.public.copy-link', { ns: 'common' }), 2000, ToastStatus.SUCCESS);
  };

  return (
    <Button variant={variant} onClick={onShareDonation} iconLeft="share">
      {children}
    </Button>
  );
};
